import React, { useId } from 'react';
import { useLingui } from '@lingui/react';

import Label from '@rover/components/src/typography/Label';
import {
  LOCATION_DEFAULT_PLACEHOLDER,
  LOCATION_DEFAULT_TITLE_1,
} from '@rover/constants/src/location/location.constants';
import LocationInputContainer from '@rover/react-lib/src/containers/LocationInputContainer';
import useDebounce from '@rover/react-lib/src/hooks/useDebounce';
import type { UpdateFiltersFunc } from '@rover/react-lib/src/pages/index/HomePage/components/SearchBox/UpdateFiltersFunc';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import type { ServiceType } from '@rover/types/src/ServiceType';

import { UpdateLocation } from '../../../utilities/analyticsEvents';

export type Props = {
  serviceType: ServiceType | undefined;
  updateFilters: UpdateFiltersFunc;
  inputHeight?: string;
  inputFontSize?: string;
  label?: string;
  placeholder?: string;
  labelMb?: string;
  labelColor?: string;
  inputValue?: string;
};

const HomePageSearchBoxLocationInput = ({
  updateFilters,
  inputHeight,
  inputFontSize,
  label,
  placeholder,
  labelMb,
  labelColor,
  inputValue,
}: Props): JSX.Element => {
  const { i18n } = useLingui();
  const id = useId();
  const debouncedLocationInputEvent = useDebounce(
    (value: string) => emitAnalyticsEvent(new UpdateLocation({ value })),
    250
  );
  return (
    <>
      <Label mb={labelMb} color={labelColor} htmlFor={id}>
        {label ?? i18n._(LOCATION_DEFAULT_TITLE_1)}
      </Label>
      <LocationInputContainer
        inputHeight={inputHeight}
        {...(inputFontSize ? { inputFontSize } : {})}
        enableLatLng
        inputId={id}
        {...(inputValue ? { inputValue } : {})}
        placeholder={placeholder ?? i18n._(LOCATION_DEFAULT_PLACEHOLDER)}
        onInputChange={(value) => {
          debouncedLocationInputEvent.start(value);
          updateFilters({
            centerlat: undefined,
            centerlng: undefined,
            location: value,
            locationType: undefined,
            rawLocationTypes: undefined,
          });
        }}
        onChange={({ value, lat, lng, locationType, rawLocationTypes }) => {
          emitAnalyticsEvent(new UpdateLocation({ value }));
          updateFilters({
            centerlat: lat,
            centerlng: lng,
            location: value,
            locationType,
            rawLocationTypes,
          });
        }}
        onClear={() => {
          emitAnalyticsEvent(new UpdateLocation({ value: '' }));
          updateFilters({
            centerlat: undefined,
            centerlng: undefined,
            location: undefined,
            locationType: undefined,
            rawLocationTypes: undefined,
          });
        }}
        validationType="popover"
      />
    </>
  );
};

export default HomePageSearchBoxLocationInput;
